import Box from '@mui/material/Box/Box'
import CircularProgress from '@mui/material/CircularProgress/CircularProgress'
import { ReactComponent as PlusLogo } from 'assets/plan_edit_plus_logo.svg'
import axAPI from 'httpConfig'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PlanApiService from 'services/planApiService'
import LargeCardButton from 'ui/buttons/LargeCardButton'
import { useAppDispatch } from '../../store/hooks'
import { notifyUser } from '../../store/notificationsSlice'
import { COLORS } from '../../styles/colors'
import { ButtonCancel } from '../../ui/buttons/ButtonCancel'
import { ButtonUI } from '../../ui/buttons/ButtonUI'
import EditPlanPageCleaning from './EditPlanPageCleaning'
import EditPlanPageDistribution from './EditPlanPageDistribution'
import EditPlanPageNudge from './EditPlanPageNudge'
import NotifBox from './NotifBox'
interface IFeedPlanType {
	active: boolean
	deviation: number
	distributed_food_offset: number
	id: number
	time: string
	type: string
	weight: number
	feed_fact: IFeedFact[]
	groups?: IFeedPlanGroup[]
}
interface IFeedPlanGroup {
	correction_percent: number
	group: IFeedPlanGroupDetails
	weight_percent: number
}
interface IFeedPlanGroupDetails {
	id: number
}
interface IFeedFact {
	date: string
	food_weight: number
	plan_time: string
	plan_weight: number
	plan_weight_percent: number
	group: IFeedFactGroup
}
interface IFeedFactGroup {
	cattles_qty: number
	id: number
	title: string
	barn: IFeedFactGroupBarn
	ration: IFeedFactGroupRation
}

interface IFeedFactGroupBarn {
	company: number
	id: number
	title: string
}
interface IFeedFactGroupRation {
	title: string
	id: number
	components: IFeedFactGroupRationComponents[]
}
interface IFeedFactGroupRationComponents {
	item: string
	coef: number
	itemKey: string
}
interface IDeviation {
	deviationDistribution: number
	deviationCleaning: number
	deviationNudge: number
}

const filterFeedPlanByAllowedGroups = (
	planData: IFeedPlanType[],
	allowedGroupIds: Set<number>,
) => {
	return planData
		.map(plan => ({
			...plan,
			groups: plan.groups?.filter(groupItem =>
				allowedGroupIds.has(groupItem.group.id),
			),
		}))
		.filter(plan => plan.groups?.length)
}

const buildPlanSignature = (plans: any[]) =>
	JSON.stringify(
		[...(plans ?? [])]
			.map(plan => ({
				type: plan.type ?? '',
				group_id: Number(plan.group_id ?? 0),
				time: plan.time ?? '',
				deviation: Number(plan.deviation ?? 0),
				weight_percent:
					plan.weight_percent == null ? null : Number(plan.weight_percent),
				correction_percent:
					plan.correction_percent == null
						? null
						: Number(plan.correction_percent),
			}))
			.sort((a, b) => {
				if (a.type !== b.type) return a.type.localeCompare(b.type)
				if (a.group_id !== b.group_id) return a.group_id - b.group_id
				if (a.time !== b.time) return a.time.localeCompare(b.time)
				if (a.weight_percent !== b.weight_percent) {
					if (a.weight_percent == null) return -1
					if (b.weight_percent == null) return 1
					return a.weight_percent - b.weight_percent
				}
				if (a.correction_percent !== b.correction_percent) {
					if (a.correction_percent == null) return -1
					if (b.correction_percent == null) return 1
					return a.correction_percent - b.correction_percent
				}
				return a.deviation - b.deviation
			}),
	)

const EditPlanPage = props => {
	const [feedPlanDistribution, setFeedPlanDistribution] =
		useState<IFeedPlanType[]>()
	const [feedPlanCleaning, setFeedPlanCleaning] = useState<IFeedPlanType[]>()
	const [feedPlanNudge, setFeedPlanNudge] = useState<IFeedPlanType[]>()
	const [adapterFeedPlan, setAdapterFeedplan] = useState<any[]>([])
	const [adapterNudgePlan, setAdapterNudgePlan] = useState<any[]>([])

	const [finallArrayForPost, setFinallArrayForPost] = useState<any[]>([])
	const [initialPlanSignature, setInitialPlanSignature] = useState<
		string | null
	>(null)
	const [deviationArray, setDeviationArray] = useState<IDeviation>({
		deviationDistribution: 0,
		deviationCleaning: 0,
		deviationNudge: 0,
	})
	const isFullCleaningPlan = feedPlanCleaning?.length > 0
	const isFullNudgePlan = feedPlanNudge?.length > 0

	const [showCleaningPlan, setShowCleaningPlan] = useState<boolean | null>(null)
	const [showNudgePlan, setShowNudgePlan] = useState<boolean | null>(
		isFullNudgePlan,
	)

	useEffect(() => {
		setShowCleaningPlan(isFullCleaningPlan)
	}, [isFullCleaningPlan])
	useEffect(() => {
		setShowNudgePlan(isFullNudgePlan)
	}, [isFullNudgePlan])

	const today = new Date()
	const tomorrow = new Date(today)
	tomorrow.setDate(tomorrow.getDate() + 1)

	useEffect(() => {
		Promise.all([
			PlanApiService.getFeedplansEdit(tomorrow.toISOString().slice(0, 10)),
			axAPI.get('/groups/list', {
				params: { foodtable_only: true },
			}),
		]).then(([feedPlanResponse, groupsResponse]) => {
			const allowedGroupIds = new Set(
				(groupsResponse.data as { id: number }[]).map(group => group.id),
			)
			const filteredFeedPlanData = filterFeedPlanByAllowedGroups(
				feedPlanResponse.data,
				allowedGroupIds,
			)
			const distribution = filteredFeedPlanData.filter(
				el => el.type === 'distribution',
			)
			const cleaning = filteredFeedPlanData.filter(el => el.type === 'cleaning')
			const nudge = filteredFeedPlanData.filter(el => el.type === 'nudge')
			setFeedPlanDistribution(distribution)
			setFeedPlanCleaning(cleaning)
			setFeedPlanNudge(nudge)
			setDeviationArray(prevData => ({
				...prevData,
				deviationDistribution:
					distribution?.length > 0 ? distribution[0].deviation : 0,
				deviationCleaning: cleaning?.length > 0 ? cleaning[0].deviation : 0,
				deviationNudge: nudge?.length > 0 ? nudge[0].deviation : 0,
			}))
		})
	}, [])

	const isErrorState = adapterNudgePlan?.some(el => el.errorValuesCell === true)
	const isValidTimeValue = (time: unknown) =>
		typeof time === 'string' &&
		/^([01]\d|2[0-3]):[0-5]\d$/.test(time) &&
		!time.includes('_')

	const normalizeNumericValue = (value: unknown) => {
		if (value === '' || value == null) return null
		const numericValue = Number(value)
		return Number.isFinite(numericValue) ? numericValue : null
	}

	const isDistributionGroupValid = (group: any) => {
		const timeValues = Array.isArray(group.time) ? group.time : []
		const weightValues = Array.isArray(group.weightPercent)
			? group.weightPercent
			: []
		const slotsLength = Math.max(timeValues.length, weightValues.length)
		let weightSum = 0
		let hasStartedSlot = false

		for (let index = 0; index < slotsLength; index++) {
			const timeValue = timeValues[index] ?? ''
			const weightValue = weightValues[index]
			const hasTime =
				timeValue !== '' && timeValue !== '__:__' && timeValue !== '__.__'
			const numericWeight = normalizeNumericValue(weightValue)
			const hasWeight = numericWeight !== null

			if (hasTime || hasWeight) {
				hasStartedSlot = true
			}

			if (hasTime !== hasWeight) {
				return false
			}

			if (hasTime && !isValidTimeValue(timeValue)) {
				return false
			}

			if (hasWeight) {
				if (numericWeight <= 0) return false
				weightSum += numericWeight
			}
		}

		if (!hasStartedSlot) return false
		return Math.abs(weightSum - 100) < 0.001
	}

	const hasInvalidSupplementaryTimes = (group: any) => {
		const cleaningValues = Array.isArray(group.timeCleaning)
			? group.timeCleaning
			: []
		const nudgeValues = Array.isArray(group.timeNudge) ? group.timeNudge : []
		const allValues = [...cleaningValues, ...nudgeValues]

		return allValues.some(timeValue => {
			if (timeValue === '' || timeValue == null) return false
			return !isValidTimeValue(timeValue)
		})
	}

	const hasValidFormData = useMemo(() => {
		if (!adapterNudgePlan || adapterNudgePlan.length === 0) return false
		if (adapterNudgePlan.some(group => hasInvalidSupplementaryTimes(group))) {
			return false
		}
		return adapterNudgePlan.every(group => isDistributionGroupValid(group))
	}, [adapterNudgePlan])

	const currentPlanSignature = useMemo(
		() => buildPlanSignature(finallArrayForPost),
		[finallArrayForPost],
	)
	const hasPlanChanges =
		initialPlanSignature !== null &&
		currentPlanSignature !== initialPlanSignature

	const isSaveDisabled =
		!hasValidFormData ||
		isErrorState ||
		finallArrayForPost.length === 0 ||
		!hasPlanChanges

	useEffect(() => {
		if (isErrorState === false && adapterNudgePlan?.length > 0) {
			const isSavableDistribution = (time: unknown, weight: unknown) => {
				if (typeof time !== 'string' || !time || time.includes('_')) {
					return false
				}
				if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) return false
				if (weight === '' || weight == null) return false
				const numericWeight = Number(weight)
				return Number.isFinite(numericWeight) && numericWeight > 0
			}

			const newArray = []
			for (let i = 0; i < adapterNudgePlan?.length; i++) {
				for (let j = 0; j < adapterNudgePlan[i].time?.length; j++) {
					if (
						isSavableDistribution(
							adapterNudgePlan[i].time[j],
							adapterNudgePlan[i].weightPercent[j],
						)
					) {
						const newObj = {
							type: 'distribution',
							deviation: deviationArray.deviationDistribution,
							group_id: adapterNudgePlan[i].group_id,
							time: adapterNudgePlan[i].time[j],
							weight_percent: Number(adapterNudgePlan[i].weightPercent[j]),
							correction_percent: +adapterNudgePlan[i].correctionPercent,
						}
						newArray.push(newObj)
					}
				}
			}

			for (let i = 0; i < adapterNudgePlan?.length; i++) {
				for (let j = 0; j < adapterNudgePlan[i].timeCleaning?.length; j++) {
					if (adapterNudgePlan[i].timeCleaning[j] !== '') {
						const newObj = {
							type: 'cleaning',
							deviation: deviationArray.deviationCleaning,
							group_id: adapterNudgePlan[i].group_id,
							time: adapterNudgePlan[i].timeCleaning[j],
						}
						newArray.push(newObj)
					}
				}
			}

			for (let i = 0; i < adapterNudgePlan?.length; i++) {
				for (let j = 0; j < adapterNudgePlan[i].timeNudge?.length; j++) {
					if (
						adapterNudgePlan[i].deviationTimeNudge === undefined &&
						adapterNudgePlan[i].timeNudge[j] !== ''
					) {
						const newObj = {
							type: 'nudge',
							deviation: deviationArray.deviationNudge,
							group_id: adapterNudgePlan[i].group_id,
							time: adapterNudgePlan[i].timeNudge[j],
						}
						newArray.push(newObj)
					}
				}
			}
			setFinallArrayForPost(newArray)
			setInitialPlanSignature(prevState =>
				prevState === null ? buildPlanSignature(newArray) : prevState,
			)
		} else {
			setFinallArrayForPost([])
		}
	}, [adapterNudgePlan, isErrorState, deviationArray])

	const navigate = useNavigate()
	const urlParams = useParams()
	const cancelButton = () => {
		navigate(`/feed/plan/`)
	}
	const dispatch = useAppDispatch()
	const saveChangeAtServer = () => {
		PlanApiService.postChangedPlans(finallArrayForPost)
			.then(response => {
				navigate(
					props.isFirstTry ? `/fill/${urlParams.id}/done` : '/feed/plan',
					{ state: { showNotification: true } },
				)
				!props.isFirstTry &&
					dispatch(
						notifyUser({
							title: 'План кормления',
							description: 'Изменения успешно сохранены',
							isError: false,
						}),
					)
			})
			.catch(error => {
				dispatch(
					notifyUser({
						title: 'План кормления',
						description: 'Ошибка при сохранении плана кормления',
						isError: true,
					}),
				)
			})
	}
	if (!feedPlanDistribution) return <></>
	const removePlan = (plan: string) => {
		setAdapterFeedplan(prevState => {
			const newState = prevState.map(item => {
				if (item[plan]) {
					return {
						...item,
						[plan]: [''],
					}
				}
				return item
			})
			return newState
		})
		setAdapterNudgePlan(prevState => {
			const newState = prevState.map(item => {
				if (item[plan]) {
					return {
						...item,
						[plan]: [''],
					}
				}
				return item
			})
			return newState
		})
		plan === 'timeCleaning' && setShowCleaningPlan(false)
		plan === 'timeNudge' && setShowNudgePlan(false)
	}
	return (
		<>
			{!feedPlanDistribution ||
			!feedPlanNudge ||
			!feedPlanCleaning ||
			!adapterFeedPlan ? (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<CircularProgress
						sx={{
							'&.MuiCircularProgress-colorPrimary': { color: COLORS.main },
						}}
					/>
				</Box>
			) : (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '24px',
						paddingLeft: props.isFirstTry ? '0px' : '20px',
						margin: '0px',
						width: '81vw',
					}}
				>
					{!props.isFirstTry && <NotifBox />}
					<EditPlanPageDistribution
						isFirstTry={props.isFirstTry}
						setDeviationArray={setDeviationArray}
						setFeedPlanDistribution={setFeedPlanDistribution}
						feedPlanDistribution={feedPlanDistribution}
						setAdapterFeedplan={setAdapterFeedplan}
						setAdapterNudgePlan={setAdapterNudgePlan}
					/>
					{showCleaningPlan && (
						<EditPlanPageCleaning
							removePlan={removePlan}
							setDeviationArray={setDeviationArray}
							setFeedPlanCleaning={setFeedPlanCleaning}
							feedPlanCleaning={feedPlanCleaning}
							adapterFeedPlan={adapterFeedPlan}
							adapterNudgePlan={adapterNudgePlan}
							setAdapterNudgePlan={setAdapterNudgePlan}
						/>
					)}
					{showNudgePlan && (
						<EditPlanPageNudge
							removePlan={removePlan}
							setDeviationArray={setDeviationArray}
							feedPlanNudge={feedPlanNudge}
							adapterNudgePlan={adapterNudgePlan}
							setAdapterNudgePlan={setAdapterNudgePlan}
							adapterFeedPlan={adapterFeedPlan}
							setFeedPlanNudge={setFeedPlanNudge}
						/>
					)}
					{!showCleaningPlan && (
						<LargeCardButton
							title={'Добавить уборку корма'}
							onClick={() => setShowCleaningPlan(true)}
							endAdornment={<PlusLogo stroke={COLORS.secondary} />}
						/>
					)}
					{!showNudgePlan && (
						<LargeCardButton
							title={'Добавить подталкивание корма'}
							onClick={() => setShowNudgePlan(true)}
							endAdornment={<PlusLogo stroke={COLORS.secondary} />}
						/>
					)}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'end',
						}}
					>
						{!props.isFirstTry && (
							<ButtonCancel onClick={cancelButton} sx={{ marginRight: '16px' }}>
								Отменить
							</ButtonCancel>
						)}
						<ButtonUI
							onClick={saveChangeAtServer}
							disabled={isSaveDisabled}
							sx={{ height: '56px' }}
						>
							{props.isFirstTry
								? 'Сохранить и завершить заполнение'
								: 'Сохранить'}
						</ButtonUI>
					</Box>
				</Box>
			)}
		</>
	)
}

export default EditPlanPage
